/**
 * Tanya Shatenko's Square service variation IDs (Ottawa catalog).
 *
 * The Karine Clinics Square account stores Tanya's bookable services as
 * 6 distinct variations — one per duration bucket plus the two free
 * intake variants. The booking flow uses this map to *explicitly* pick
 * the right variation when a deep-link from /laser-and-skin specifies
 * a duration, so Square always lands on the correct bookable item
 * (instead of relying on Square's fuzzy fallback by name).
 *
 * Captured Jun 17 2026 — see CHANGELOG entry "Tanya's Square service
 * variations" for the audit trail. If Karine ever recreates Tanya's
 * services in Square, update these IDs here and the booking flow
 * picks the new ones automatically.
 */

export const ANNA_TEAM_MEMBER_ID = "TM0vnKbedKJ-2eOo";

// Duration (minutes) → Square service_variation_id for paid services.
export const ANNA_DURATION_VARIATIONS = {
  30: "QYZX57W2ES4NWGRDTT6MLN2E", // 30-minute treatment
  45: "T2SM6VETXSXJFCWX2VA346AV", // 45-minute treatment
  60: "U37KFCDIGASRKIO4EAJKQT6C", // 60-minute treatment
};

// Free / special intake services
export const ANNA_FREE_CONSULT_15 = "KV2YTYMSGH2R2IZA3BMBDVS5"; // 15-min consult
export const ANNA_FREE_CONSULT_30 = "CQ6OQUNBHQSQZVZIGR3IFJG7"; // 30-min consult
export const ANNA_LYMPHATIC_DRAIN_30 = "BX6PHRBNUPHJFRQ6OU75VE2B"; // Jun 19 event freebie

/**
 * Resolve the right Tanya variation ID for a given booking-package
 * pre-fill. Returns null when no rule matches — caller falls back to
 * showing Tanya's catalog and letting the user pick manually.
 *
 * Rules (priority order):
 *  1. `bookingType === "consultation"` and `duration <= 15`  → 15-min free consult
 *  2. `bookingType === "consultation"`                       → 30-min free consult
 *  3. `bookingType === "lymphatic"` (Jun 19 event)           → lymphatic drain
 *  4. `duration` in {30, 45, 60}                              → paid bucket
 */
export function resolveTanyaVariation({ duration, bookingType, eventId } = {}) {
  const t = (bookingType || "").toLowerCase();
  const d = parseInt(duration || 0, 10);
  if (t === "consultation") {
    return d > 0 && d <= 15 ? ANNA_FREE_CONSULT_15 : ANNA_FREE_CONSULT_30;
  }
  if (t === "lymphatic" || eventId === "lymphatic-jun19") {
    return ANNA_LYMPHATIC_DRAIN_30;
  }
  if (d in ANNA_DURATION_VARIATIONS) {
    return ANNA_DURATION_VARIATIONS[d];
  }
  // Round up to nearest bucket (e.g. 50 → 60) for safety on duration drift
  if (d > 0) {
    const buckets = [30, 45, 60].sort((a, b) => a - b);
    const next = buckets.find((b) => b >= d) || buckets[buckets.length - 1];
    return ANNA_DURATION_VARIATIONS[next];
  }
  return null;
}
